import ListItem from "./ListItem"
import { useState, useEffect } from "react"
import { useLocation } from "react-router-dom"
import { authFetch } from "../utils/api"
import { useUser } from "../context/UserContext"

const ESTADOS_FILTRO = [
  { value: "",             label: "todos" },
  { value: "PENDIENTE",   label: "pendiente" },
  { value: "DISTRIBUCION",label: "en distribución" },
  { value: "ENTREGADO",   label: "entregado" },
  { value: "CANCELADO",   label: "cancelado" },
  { value: "SIN_PROCESOS",label: "sin envíos" },
]

const POR_PAGINA = 20

function PatientListContainer() {
  const [patients, setPatients] = useState([])
  const [searchValue, setSearchValue] = useState("")
  const [filtroEstado, setFiltroEstado] = useState("")
  const [filtroObraSocial, setFiltroObraSocial] = useState("")
  const [pagina, setPagina] = useState(1)
  const { user } = useUser()
  const isAdmin = user?.role === "ADMIN"
  const location = useLocation()

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const res = await authFetch(`${process.env.REACT_APP_API_URL}/pacientes`)
        if (!res.ok) throw new Error("Error al traer pacientes")
        const data = await res.json()
        setPatients(data)
      } catch (err) {
        console.error(err)
      }
    }
    fetchPatients()
  }, [location.key])

  const normalize = (str) =>
    str
      ?.toLowerCase()
      .replace(/,/g, "")
      .replace(/\s+/g, " ")
      .trim()

  const obrasSocialesDisponibles = [...new Set(patients.map(p => p.prestador).filter(Boolean))].sort()

  const filteredPatients = patients.filter((patient) => {
    if (searchValue) {
      const value = normalize(searchValue)
      const nombre1 = normalize(`${patient.nombre} ${patient.apellido}`)
      const nombre2 = normalize(`${patient.apellido}, ${patient.nombre}`)
      const matchSearch =
        patient.dni?.toString().includes(value) ||
        patient.nroSocio?.toString().includes(value) ||
        nombre1.includes(value) ||
        nombre2.includes(value)
      if (!matchSearch) return false
    }
    if (filtroEstado && patient.estado !== filtroEstado) return false
    if (filtroObraSocial && patient.prestador !== filtroObraSocial) return false
    return true
  })

  const mostrarPaginacion = filteredPatients.length > POR_PAGINA
  const totalPaginas = Math.max(1, Math.ceil(filteredPatients.length / POR_PAGINA))
  const paginaReal = Math.min(pagina, totalPaginas)
  const patientsPagina = mostrarPaginacion
    ? filteredPatients.slice((paginaReal - 1) * POR_PAGINA, paginaReal * POR_PAGINA)
    : filteredPatients

  const handleSearch = (e) => { setSearchValue(e.target.value); setPagina(1) }
  const handleFiltroEstado = (val) => { setFiltroEstado(val); setPagina(1) }
  const handleFiltroOS = (e) => { setFiltroObraSocial(e.target.value); setPagina(1) }

  return (
    <div className="pageContainer">
      <div className="patientList column">
        <div className="patientListSearch row alignStart">
          <i className="fa-solid fa-magnifying-glass"></i>
          <label htmlFor="searchBox">Búsqueda</label>
          <input
            type="text"
            value={searchValue}
            onChange={handleSearch}
          />
        </div>

        <div className="patientListFilters row">
          <div className="filterChips row">
            {ESTADOS_FILTRO.map(opt => (
              <button
                key={opt.value}
                className={`filterChip${filtroEstado === opt.value ? " filterChip--active" : ""}`}
                onClick={() => handleFiltroEstado(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {isAdmin && (
            <select
              className="filterSelect"
              value={filtroObraSocial}
              onChange={handleFiltroOS}
            >
              <option value="">todas las obras sociales</option>
              {obrasSocialesDisponibles.map(os => (
                <option key={os} value={os}>{os}</option>
              ))}
            </select>
          )}
        </div>

        <div className="patientListTable">
          <div className={`patientListTitle row alignStart${isAdmin ? " patientListTitle--admin" : ""}`}>
            <div>nombre</div>
            {isAdmin && <div>prestador</div>}
            <div>número de afiliado</div>
            <div>dni</div>
            <div>estado del último proceso</div>
          </div>

          <div className="patientListItemContainer column">
            {patientsPagina.map((patient) => (
              <ListItem key={patient.id} patient={patient} showPrestador={isAdmin} />
            ))}
          </div>
        </div>

        {mostrarPaginacion && (
          <div className="patientListPaginacion">
            <div className="patientListPaginacionBtns">
              <button
                className="paginaBtn paginaBtn--nav"
                onClick={() => setPagina(p => Math.max(1, p - 1))}
                disabled={paginaReal === 1}
              >
                ‹
              </button>
              {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  className={`paginaBtn${n === paginaReal ? " paginaBtn--active" : ""}`}
                  onClick={() => setPagina(n)}
                >
                  {n}
                </button>
              ))}
              <button
                className="paginaBtn paginaBtn--nav"
                onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))}
                disabled={paginaReal === totalPaginas}
              >
                ›
              </button>
            </div>
            <span className="patientListPaginacionInfo">
              {(paginaReal - 1) * POR_PAGINA + 1}–{Math.min(paginaReal * POR_PAGINA, filteredPatients.length)} de {filteredPatients.length}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default PatientListContainer
